import React, { useState, useEffect, useCallback } from "react";
import PaymentMethod from "../components/PaymentMethod";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ErrorTransactionNotFound from "../components/ErrorTransactionNotFound";
import Notification from "../components/Notification";
import OvoPhoneModal from "../components/OvoPhoneModal";
import { messaging, onMessage, registerServiceWorker, requestToken } from "../../config/firebaseConfig";
import { setInquiryDetails } from "../../redux/slices/inquirySlice";
import { setNotification } from "../../redux/slices/notificationSlice";
import { updateCustomerDetail } from "../../redux/slices/customerSlice";
import { authorizedFetch, SessionExpiredError } from "../../utils/authorizedFetch";
import { getMethodSelectionExpiry } from "../../utils/paymentLinkExpiration";
import { PaymentLinkErrorCode, getErrorMessage } from "../../utils/paymentLinkErrorHandler";
import { getPreInquiryRequirements, resolvePostInquiryNavigation } from "../utils/ewalletFlowRouter";
import { getExpiredRedirectUrl, getSuccessRedirectUrl, redirectToMerchantUrl } from "../utils/paymentLinkRedirects";
import ThreeDSChallengeModal from "../components/ThreeDSChallengeModal";
import useTranslation from "../hooks/useTranslation";
import { pickThreeDSChallengeContent } from "../utils/threeDSChallengeContent";

const BASE_URL = process.env.REACT_APP_API_URL;
// How often to re-check the inline QRIS status when we couldn't get an FCM token (permission
// denied, unsupported browser, etc.) — the fallback for the onMessage auto-redirect below.
const STATUS_POLL_INTERVAL_MS = 5000;
// Faster than the QRIS fallback: Midtrans ACS navigations should not wait 5s
// for the next tick when iframe onLoad already fired.
const THREE_DS_POLL_INTERVAL_MS = 4000;

const SecondForm = () => {
  // untuk terima notif di background firebase — same as ThirdForm, needed so the
  // inline QRIS flow below can auto-redirect on payment without navigating away.
  registerServiceWorker();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { reffno } = useParams();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [isFcmTokenLoading, setIsFcmTokenLoading] = useState(true);
  const [fcmTokenError, setFcmTokenError] = useState(null);
  const [showOvoPhoneModal, setShowOvoPhoneModal] = useState(false);
  const [pendingOvoMethodCode, setPendingOvoMethodCode] = useState(null);
  const [qrisInquiryResult, setQrisInquiryResult] = useState(null);
  const [isQrisGenerating, setIsQrisGenerating] = useState(false);
  const [qrisHistoryId, setQrisHistoryId] = useState(null);
  const [show3DSModal, setShow3DSModal] = useState(false);
  const [threeDSContent, setThreeDSContent] = useState(null);
  const [pendingCardInquiry, setPendingCardInquiry] = useState(null);
  const [cardPayError, setCardPayError] = useState(null);

  // Ambil data dari Redux store
  const paymentData = useSelector((state) => state.payment.data);
  const paymentLinkToken = useSelector((state) => state.payment.token);
  const customerData = useSelector((state) => state.customer);
  const errorCode = useSelector((state) => state.payment.errorCode);
  const paymentError = useSelector((state) => state.payment.error);
  const notification = useSelector((state) => state.notification);

  // Fetch token FCM saat komponen dimount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        setIsFcmTokenLoading(true);
        setFcmTokenError(null);
        const token = await requestToken();
        setFcmToken(token);
      } catch (err) {
        setFcmTokenError(err.message);
      } finally {
        setIsFcmTokenLoading(false);
      }
    };

    fetchToken();
  }, []);

  // Fetch payment methods dari API (GET request)
  useEffect(() => {
    if (!paymentData) return;
    console.log("Fetching payment methods for paymentData:", paymentData);

    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const response = await authorizedFetch(`${BASE_URL}/api/payment-link/available-payment-method`, {
          dispatch,
          token: paymentLinkToken,
          refresh: { kind: "link", reffNo: paymentData.reff_no },
          method: "POST", // Ubah dari GET ke POST
          body: JSON.stringify({
            payment_link_id: paymentData.id, // Kirim ID di body
            reff_no: paymentData.reff_no, // Required by middleware
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch payment methods");
        }

        const data = await response.json();
        setPaymentMethods(
          data.data.map((method) => ({
            code: method.code.toUpperCase(),
            name: method.name,
            desc: method.desc,
            group: method.group || null,
            vendor_fee: method.vendor_fee,
            our_margin: method.our_margin,
            merchant_fee: method.merchant_fee,
            card_checkout_mode: method.card_checkout_mode || null,
          })),
        );
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        setError(
          err instanceof SessionExpiredError
            ? getErrorMessage(PaymentLinkErrorCode.SESSION_LOST)
            : t("secondForm.failedLoadMethods"),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, [paymentData, paymentLinkToken, dispatch, t]);

  const proceedWithInquiry = useCallback(
    async (methodCode, overridePhone, options = {}) => {
      if (!paymentData) return;

      const effectiveCustomerData = {
        ...(options.customer || customerData),
        ...(overridePhone ? { phone: overridePhone } : {}),
      };

      setIsProcessing(true);
      try {
        const response = await authorizedFetch(`${BASE_URL}/api/payment-link/proceed-inquiry`, {
          dispatch,
          token: paymentLinkToken,
          refresh: { kind: "link", reffNo: paymentData.reff_no },
          method: "POST",
          body: JSON.stringify({
            reff_no: paymentData.reff_no,
            payment_method_code: methodCode,
            customer_data: JSON.stringify(effectiveCustomerData),
            fcm_token: fcmToken,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || "Payment processing failed");
        }

        dispatch(
          setInquiryDetails({
            amount: paymentData.total_amount,
            orderId: result.data.paymentLinkHistory.reff_no,
            merchantName: result.data.paymentLink.account.merchant.name,
            expired_at: result.data.paymentLinkHistory.expired_at,
            selected_method: result.data.paymentLinkHistory.payment_method_name,
            paymentMethod: result.data.paymentMethod,
            paymentLink: result.data.paymentLink,
            paymentLinkHistory: result.data.paymentLinkHistory,
          }),
        );

        if (options.skipNavigate) {
          return result;
        }

        const nav = resolvePostInquiryNavigation(result, paymentData, effectiveCustomerData);
        navigate(nav.route, { state: nav.state });
        return result;
      } catch (err) {
        dispatch(
          setNotification({
            type: "error",
            title: t("secondForm.notifErrorTitle"),
            message: err.message || t("secondForm.notifErrorGenericMessage"),
          }),
        );
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [paymentData, paymentLinkToken, customerData, fcmToken, dispatch, navigate, t],
  );

  const handleSelectPaymentMethod = useCallback(
    (methodCode) => {
      const { requiresPhone } = getPreInquiryRequirements(methodCode);
      if (requiresPhone && !customerData.phone) {
        setPendingOvoMethodCode(methodCode);
        setShowOvoPhoneModal(true);
        return;
      }
      proceedWithInquiry(methodCode);
    },
    [customerData.phone, dispatch, proceedWithInquiry],
  );

  const handleGenerateQris = useCallback(
    async (methodCode) => {
      dispatch(
        setNotification({
          type: "info",
          title: t("secondForm.preparingSessionNotifTitle"),
          message: t("secondForm.preparingSessionNotifMessage"),
        }),
      );

      if (qrisInquiryResult?.data?.paymentMethod?.code === methodCode) {
        return;
      }

      setIsQrisGenerating(true);
      try {
        const result = await proceedWithInquiry(methodCode, undefined, { skipNavigate: true });
        setQrisInquiryResult(result);
        setQrisHistoryId(result?.data?.paymentLinkHistory?.id || null);
      } catch {
        // Notification is already handled inside proceedWithInquiry.
      } finally {
        setIsQrisGenerating(false);
      }
    },
    [isFcmTokenLoading, fcmToken, qrisInquiryResult, dispatch, proceedWithInquiry, t],
  );

  // Same auto-redirect-on-payment logic as ThirdForm's checkTransactionStatus,
  // adapted for the inline QRIS flow (customer never navigates away from
  // SecondForm to see the QR, so nothing here ever detected a completed payment).
  // `stopPolling` lets the background poll below tear down its own interval once a terminal
  // status is hit; the manual "Check Payment Status" button call omits it.
  const checkQrisTransactionStatus = useCallback(async ({ stopPolling } = {}) => {
    if (!qrisHistoryId) return;

    const checkReffNo = qrisInquiryResult?.data?.paymentLinkHistory?.reff_no;

    try {
      const response = await authorizedFetch(`${BASE_URL}/api/payment-link/check-status`, {
        dispatch,
        token: paymentLinkToken,
        refresh: { kind: "history", reffNo: checkReffNo },
        method: "POST",
        body: JSON.stringify({
          history_id: qrisHistoryId,
          reff_no: checkReffNo, // Required by middleware
        }),
      });

      const data = await response.json();
      const history_check = data.transaction;

      if (history_check.status === "paid") {
        stopPolling?.();
        dispatch(
          setNotification({
            type: "success",
            title: t("secondForm.statusNotifTitle"),
            message: t("secondForm.paymentReceivedMessage"),
          }),
        );

        setTimeout(() => {
          const successRedirectUrl =
            history_check.success_redirect_url || getSuccessRedirectUrl(qrisInquiryResult?.data?.paymentLink);

          if (redirectToMerchantUrl(successRedirectUrl)) {
            return;
          }

          navigate("/payment-success", {
            replace: true,
            state: {
              reffNo: history_check.reff_no || "-",
              amount: history_check.amount || "-",
              customerName: history_check.customer_name || null,
              email: history_check.customer_email || null,
              paymentMethod: history_check.payment_method || "-",
              paymentDate: history_check.payment_date || "-",
              redirectUrl: successRedirectUrl,
            },
          });
        }, 3000);
      } else if (history_check.status === "expired") {
        stopPolling?.();
        dispatch(
          setNotification({
            type: "warning",
            title: t("secondForm.statusNotifTitle"),
            message: `${t("secondForm.statusIsPrefix")} ${history_check.status}`,
          }),
        );

        setTimeout(() => {
          const expiredRedirectUrl =
            history_check.expired_redirect_url || getExpiredRedirectUrl(qrisInquiryResult?.data?.paymentLink);

          if (redirectToMerchantUrl(expiredRedirectUrl)) {
            return;
          }

          navigate("/payment-expired", {
            replace: true,
            state: {
              selectedMethod: history_check.payment_method || "-",
              reffNo: history_check.reff_no || "-",
              amount: history_check.amount || "-",
              redirectUrl: expiredRedirectUrl,
            },
          });
        }, 3000);
      }
    } catch (error) {
      console.error("Error checking QRIS transaction status:", error);
    }
  }, [qrisHistoryId, qrisInquiryResult, paymentLinkToken, dispatch, navigate, t]);

  useEffect(() => {
    if (!qrisHistoryId) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      const status = payload?.data?.status;
      if (status === "paid" || status === "expired") {
        checkQrisTransactionStatus();
      } else {
        dispatch(
          setNotification({
            type: "info",
            title: t("secondForm.paymentNotifTitle"),
            message: payload?.notification?.body,
          }),
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [qrisHistoryId, checkQrisTransactionStatus, dispatch, t]);

  // Fallback for customers who never granted (or were never asked for) notification
  // permission — the onMessage listener above will simply never fire for them, so once the
  // FCM token fetch has settled and came back empty, poll check-status directly instead.
  useEffect(() => {
    if (!qrisHistoryId) return;
    if (isFcmTokenLoading || fcmToken) return;

    let stopped = false;
    const timerId = setInterval(() => {
      if (stopped) return;
      checkQrisTransactionStatus({
        stopPolling: () => {
          stopped = true;
          clearInterval(timerId);
        },
      });
    }, STATUS_POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(timerId);
    };
  }, [qrisHistoryId, isFcmTokenLoading, fcmToken, checkQrisTransactionStatus]);

  const handleOvoPhoneSubmit = useCallback(
    (phone) => {
      dispatch(updateCustomerDetail({ phone }));
      setShowOvoPhoneModal(false);
      if (pendingOvoMethodCode) {
        proceedWithInquiry(pendingOvoMethodCode, phone);
        setPendingOvoMethodCode(null);
      }
    },
    [pendingOvoMethodCode, dispatch, proceedWithInquiry],
  );

  const handleSubmitCardPayment = useCallback(
    async (methodCode, cardPayload) => {
      setCardPayError(null);

      try {
        const inquiryResult = await proceedWithInquiry(methodCode, undefined, { skipNavigate: true });

        if (!inquiryResult?.data?.paymentLinkHistory?.reff_no) {
          const message = t("secondForm.failedCardSession");
          setCardPayError(message);
          throw new Error(message);
        }

        const response = await authorizedFetch(`${BASE_URL}/api/payment-link/card-payment`, {
          dispatch,
          token: paymentLinkToken,
          refresh: { kind: "history", reffNo: inquiryResult.data.paymentLinkHistory.reff_no },
          method: "POST",
          body: JSON.stringify({
            reff_no: inquiryResult.data.paymentLinkHistory.reff_no,
            ...cardPayload,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          const message = result.message || t("secondForm.cardPaymentFailedFallback");
          setCardPayError(message);
          dispatch(
            setNotification({
              type: "error",
              title: t("secondForm.notifErrorTitle"),
              message,
            }),
          );
          throw new Error(message);
        }

        // Show 3DS on this page immediately. Do NOT navigate first — putting large
        // Nicepay HTML into router state can be dropped, which hid the modal entirely.
        if (result.requires_3ds) {
          const challengeContent = pickThreeDSChallengeContent(result.data?.html_form, result.data?.payment_url);
          if (!challengeContent) {
            const message = t("secondForm.threeDsChallengeMissing");
            setCardPayError(message);
            dispatch(
              setNotification({
                type: "error",
                title: t("secondForm.notifErrorTitle"),
                message,
              }),
            );
            throw new Error(message);
          }

          setPendingCardInquiry(inquiryResult);
          setThreeDSContent(challengeContent);
          setShow3DSModal(true);
          return result;
        }

        // Frictionless / pending: wait for Nicepay webhook — do not jump to success yet.
        if (result.awaiting_webhook || result.data?.status === "pending") {
          const nav = resolvePostInquiryNavigation(inquiryResult, paymentData, customerData);
          navigate(nav.route, {
            replace: true,
            state: {
              ...nav.state,
              awaitingCardWebhook: true,
            },
          });
          return result;
        }

        const successRedirectUrl = getSuccessRedirectUrl(inquiryResult.data.paymentLink);

        if (redirectToMerchantUrl(successRedirectUrl)) {
          return result;
        }

        navigate("/payment-success", {
          replace: true,
          state: {
            reffNo: inquiryResult.data.paymentLinkHistory.reff_no,
            amount: inquiryResult.data.paymentLinkHistory.amount,
            customerName: inquiryResult.data.paymentLinkHistory.customer_name || cardPayload.card_holder_name,
            email: inquiryResult.data.paymentLinkHistory.customer_email || cardPayload.card_holder_email,
            paymentMethod: inquiryResult.data.paymentLinkHistory.payment_method_name || t("secondForm.creditDebitCard"),
            paymentDate: new Date().toISOString(),
            redirectUrl: successRedirectUrl,
          },
        });

        return result;
      } catch (err) {
        setCardPayError(err?.message || t("secondForm.cardPaymentFailedFallback"));
        throw err;
      }
    },
    [proceedWithInquiry, dispatch, navigate, paymentData, paymentLinkToken, customerData, t],
  );

  const handleSubmitHostedCard = useCallback(
    async (methodCode, details) => {
      setCardPayError(null);
      const customer = {
        ...customerData,
        fullName: details.fullName,
        email: details.email,
        phone: details.phone,
      };
      dispatch(updateCustomerDetail(customer));

      const inquiryResult = await proceedWithInquiry(methodCode, undefined, {
        skipNavigate: true,
        customer,
      });
      const reffNo = inquiryResult?.data?.paymentLinkHistory?.reff_no;
      if (!reffNo) {
        const message = t("secondForm.failedCardSession");
        setCardPayError(message);
        throw new Error(message);
      }

      const response = await authorizedFetch(`${BASE_URL}/api/payment-link/asteripay-checkout`, {
        dispatch,
        token: paymentLinkToken,
        refresh: { kind: "history", reffNo },
        method: "POST",
        body: JSON.stringify({
          reff_no: reffNo,
          full_name: details.fullName,
          email: details.email,
          phone: details.phone,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.handoff_url) {
        const message = result.message || t("secondForm.cardPaymentFailedFallback");
        setCardPayError(message);
        dispatch(
          setNotification({
            type: "error",
            title: t("secondForm.notifErrorTitle"),
            message,
          }),
        );
        throw new Error(message);
      }

      window.location.assign(result.handoff_url);
    },
    [customerData, dispatch, proceedWithInquiry, paymentLinkToken, t],
  );

  const handleThreeDSResult = useCallback(
    (data) => {
      setShow3DSModal(false);
      setThreeDSContent(null);

      if (!data?.success) {
        setPendingCardInquiry(null);
        const message = data?.resultMessage || t("secondForm.cardPaymentFailedFallback");
        setCardPayError(message);
        dispatch(
          setNotification({
            type: "error",
            title: t("secondForm.notifErrorTitle"),
            message,
          }),
        );
        return;
      }

      setCardPayError(null);
      const inquiryResult = pendingCardInquiry;
      setPendingCardInquiry(null);
      if (!inquiryResult) return;

      const nav = resolvePostInquiryNavigation(inquiryResult, paymentData, customerData);
      navigate(nav.route, {
        replace: true,
        state: {
          ...nav.state,
          awaitingCardWebhook: true,
        },
      });
    },
    [pendingCardInquiry, paymentData, customerData, navigate, dispatch, t],
  );

  // While 3DS is open, poll status — ACS may finish server-to-server without postMessage
  // (Midtrans finish page, Nicepay JSON redirectUrlHttp). iframe onLoad probes faster
  // on ACS navigations; the interval is the fallback if onLoad is swallowed.
  const pollOpenThreeDSStatus = useCallback(async () => {
    if (!show3DSModal || !pendingCardInquiry) return;
    const historyId = pendingCardInquiry.data?.paymentLinkHistory?.id;
    const reffNo = pendingCardInquiry.data?.paymentLinkHistory?.reff_no;
    if (!historyId || !reffNo) return;

    try {
      const response = await authorizedFetch(`${BASE_URL}/api/payment-link/check-status`, {
        dispatch,
        token: paymentLinkToken,
        refresh: { kind: "history", reffNo },
        method: "POST",
        body: JSON.stringify({ history_id: historyId, reff_no: reffNo }),
      });
      const data = await response.json().catch(() => ({}));
      const status = data?.transaction?.status;
      if (status === "paid" || status === "failed" || status === "expired" || status === "canceled") {
        setShow3DSModal(false);
        setThreeDSContent(null);
        const inquiryResult = pendingCardInquiry;
        setPendingCardInquiry(null);

        if (status === "paid" && inquiryResult) {
          const successRedirectUrl = getSuccessRedirectUrl(inquiryResult.data.paymentLink);
          if (redirectToMerchantUrl(successRedirectUrl)) return;

          navigate("/payment-success", {
            replace: true,
            state: {
              reffNo: inquiryResult.data.paymentLinkHistory.reff_no,
              amount: inquiryResult.data.paymentLinkHistory.amount,
              customerName: inquiryResult.data.paymentLinkHistory.customer_name || null,
              email: inquiryResult.data.paymentLinkHistory.customer_email || null,
              paymentMethod: inquiryResult.data.paymentLinkHistory.payment_method_name || t("secondForm.creditDebitCard"),
              paymentDate: new Date().toISOString(),
              redirectUrl: successRedirectUrl,
            },
          });
        } else if (status !== "paid") {
          const message = t("secondForm.cardPaymentFailedFallback");
          setCardPayError(message);
          dispatch(
            setNotification({
              type: "error",
              title: t("secondForm.notifErrorTitle"),
              message,
            }),
          );
        }
      }
    } catch (err) {
      console.error("3DS status poll error:", err);
    }
  }, [show3DSModal, pendingCardInquiry, paymentLinkToken, dispatch, navigate, t]);

  useEffect(() => {
    if (!show3DSModal || !pendingCardInquiry) return undefined;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await pollOpenThreeDSStatus();
    };

    tick();
    const intervalId = setInterval(tick, THREE_DS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [show3DSModal, pendingCardInquiry, pollOpenThreeDSStatus]);

  // Redirect to /b2b/:reffno if paymentData not found (e.g., after refresh)
  useEffect(() => {
    if (!paymentData && reffno) {
      navigate(`/b2b/${reffno}`, { replace: true });
    }
  }, [paymentData, reffno, navigate]);

  if (!paymentData) {
    return null; // Will redirect via useEffect
    // return <ErrorTransactionNotFound errorCode={errorCode} message={paymentError} />;
  }

  // Calculate countdown expiry: default 5 minutes, or PaymentLink expired_at if sooner
  const countdownExpiry = getMethodSelectionExpiry(paymentData?.expired_at, 5);

  if (process.env.REACT_APP_DEBUG) {
    console.log("SecondForm Countdown Expiry:", {
      paymentLinkExpiredAt: paymentData?.expired_at,
      fiveMinutesFromNow: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      countdownExpiry,
      source: countdownExpiry === paymentData?.expired_at ? "PaymentLink expired_at" : "5 minutes default",
    });
  }

  return (
    <div>
      {/* <Notification
        message={notification.message}
        type={notification.type}
        title={notification.title}
      /> */}
      {loading && <p className="text-center text-gray-500">{t("secondForm.loadingMethods")}</p>}
      {error && <p className="text-center text-red-500">{error}</p>}
      {isProcessing && <p className="text-center text-blue-500">{t("secondForm.processingPayment")}</p>}
      {isFcmTokenLoading && <p className="text-center text-gray-500">{t("secondForm.preparingSession")}</p>}
      {/* {!isFcmTokenLoading && !fcmToken && (
        <p className="text-center text-red-500">
          Unable to prepare payment session. Please allow notifications and refresh this page.
        </p>
      )} */}
      {!loading && !error && (
        <PaymentMethod
          orderId={paymentData.reff_no}
          paymentTo={paymentData.account.merchant.name}
          amount={paymentData.total_amount}
          paymentMethodOptions={paymentMethods}
          customerData={customerData}
          onSelectMethod={handleSelectPaymentMethod}
          onGenerateQris={handleGenerateQris}
          onCheckQrisStatus={checkQrisTransactionStatus}
          onSubmitCardPayment={handleSubmitCardPayment}
          onSubmitHostedCard={handleSubmitHostedCard}
          cardPayError={cardPayError}
          onClearCardPayError={() => setCardPayError(null)}
          expiredAt={countdownExpiry}
          qrisInquiryResult={qrisInquiryResult}
          isQrisGenerating={isQrisGenerating}
          isDisabled={isProcessing}
          disabledMessage={
            isFcmTokenLoading
              ? t("secondForm.preparingSession")
              : fcmTokenError
                ? t("secondForm.allowNotifications")
                : t("secondForm.sessionNotReady")
          }
        />
      )}
      <OvoPhoneModal
        isOpen={showOvoPhoneModal}
        onClose={() => {
          setShowOvoPhoneModal(false);
          setPendingOvoMethodCode(null);
        }}
        onSubmit={handleOvoPhoneSubmit}
        isProcessing={isProcessing}
      />
      <ThreeDSChallengeModal
        isOpen={show3DSModal}
        content={threeDSContent}
        onClose={() => {
          setShow3DSModal(false);
          setThreeDSContent(null);
        }}
        onResult={handleThreeDSResult}
        onIframeLoad={pollOpenThreeDSStatus}
      />
    </div>
  );
};

export default SecondForm;
